import React, { useEffect, useState } from 'react';
import {
  amountInputProps,
  centsToInputString,
  isValidBetAmount,
  parseCurrencyInput,
  viewerLocale,
} from '../utils/currency';
import { seatedPlayers, tableDefaultBuyIn } from '../utils/playerLedger';
import { getCachedProfileName } from '../utils/userProfile';
import { t } from '../i18n';
import { localizeError } from '../i18n/errors';
import Button from './ui/Button';
import Modal from './ui/Modal';

const fieldLabel = 'label-eyebrow mb-2';
const fieldInput =
  'w-full px-4 py-3 bg-transparent border border-line rounded-lg text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors';

function playersLine(count) {
  const n = Number.isFinite(count) ? count : 0;
  return t('invite.players', { count: n });
}

export default function InviteSeatModal({
  room,
  onClose,
  onSubmit,
  loading = false,
  error,
}) {
  const currency = room?.settings?.currency || 'USD';
  const loc = viewerLocale();
  const defaultBuyIn = tableDefaultBuyIn(room?.players, room?.hostId);
  const [name, setName] = useState(() => getCachedProfileName() || '');
  const [buyIn, setBuyIn] = useState(() => centsToInputString(defaultBuyIn, currency, loc));
  const [buyInTouched, setBuyInTouched] = useState(false);

  useEffect(() => {
    if (!buyInTouched) setBuyIn(centsToInputString(defaultBuyIn, currency, loc));
  }, [defaultBuyIn, buyInTouched, currency, loc]);

  let parsedCents = 0;
  try {
    parsedCents = parseCurrencyInput(buyIn, loc, currency);
  } catch {
    parsedCents = 0;
  }
  const submitDisabled = loading || !name.trim() || !isValidBetAmount(parsedCents, 100);
  const seatedCount = seatedPlayers(room?.players).length;
  const roomCode = room?.roomCode || '';

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (submitDisabled) return;
    onSubmit?.(roomCode, name.trim(), parsedCents);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('invite.title')}
      testId="your-seat"
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitDisabled}
            trailingArrow
            data-testid="join-submit"
          >
            {loading ? t('home.joining') : t('invite.takeSeat')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-seat-form">
        <p className="text-[13px] text-mute -mt-1" data-testid="invite-room-line">
          {t('invite.roomLine', { code: roomCode, players: playersLine(seatedCount) })}
        </p>
        <div>
          <div className={fieldLabel}>{t('invite.name')}</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('home.namePlaceholder')}
            className={fieldInput}
            maxLength={20}
            disabled={loading}
            autoComplete="off"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            autoFocus={false}
            enterKeyHint="go"
            data-testid="join-name"
          />
        </div>
        <div>
          <div className={fieldLabel}>{t('poker.buyIn')}</div>
          <input
            type="text"
            {...amountInputProps(currency)}
            value={buyIn}
            onChange={(e) => {
              setBuyInTouched(true);
              setBuyIn(e.target.value);
            }}
            placeholder={centsToInputString(defaultBuyIn, currency, loc)}
            className={`${fieldInput} tnum`}
            disabled={loading}
            data-testid="join-buyin"
            aria-label={t('poker.buyIn')}
          />
        </div>
        {error && (
          <div
            className="text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2"
            data-testid="join-error"
          >
            {localizeError(error)}
          </div>
        )}
        <button type="submit" hidden disabled={submitDisabled} />
      </form>
    </Modal>
  );
}
